/**
 * As Novidades do ERP (spec 036) — o conteúdo, versionado com o código (D1).
 *
 * Como escrever uma entrada:
 * - ela sobe no MESMO PR da mudança que anuncia (D7), com a data do dia em que
 *   a mudança vai para o ar, e entra NO TOPO (a lista é do mais novo para o
 *   mais antigo);
 * - quem lê é quem opera: o antes, o depois e onde clicar, em 50 a 150
 *   palavras. `**negrito**` no que se clica;
 * - palavra de vertical nunca vai escrita (guarda de vocabulário da spec 017):
 *   use `{resourceId}`, `{resource}`, `{contact}`, `{client}`, `{ticket}`… e
 *   `{source}` para o nome do sistema de origem do recurso. Com a primeira
 *   letra maiúscula (`{Contact}`), o termo sai capitalizado;
 * - diga para quem é (D3): `audience` e, quando houver, `requires` — papel,
 *   permissão, o que o tenant precisa ter;
 * - imagem só de dados de demonstração, em apps/web/public/novidades/ (D5);
 * - tour: cada passo aponta para um `data-tour="…"` que existe num componente
 *   (D4). A guarda apps/api/src/shared/news-entries.test.ts confere ids,
 *   datas, público, tokens, imagens, rotas e âncoras.
 */
import type { NewsEntry } from './news-model';

export const NEWS_ENTRIES: readonly NewsEntry[] = [
  {
    id: 'ausencia-automatica',
    date: '2026-11-04',
    title: 'Disponível sem atividade vira Ausente',
    summary:
      'Quem fica 1 hora sem atividade no ERP sai do Disponível e passa a Ausente. Ao voltar, um aviso pergunta se você quer ficar disponível de novo.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** o status só mudava à mão. Quem fechava o navegador em Disponível continuava Disponível por dias, e o tempo online do relatório contava esses dias.',
      },
      {
        kind: 'list',
        items: [
          'sem tecla, clique, mensagem enviada ou ação em {ticket} por 1 hora, o seu status passa a **Ausente**, contado a partir da sua última atividade;',
          'ao voltar, aparece o aviso **Você saiu do Disponível**: escolha **Ficar disponível** ou **Continuar ausente**. A sessão não fecha;',
          'a pausa não cai sozinha;',
          'na **Supervisão**, quem saiu assim e ainda tem conversa na mão aparece como **Ausente · Sem atividade**.',
        ],
      },
      {
        kind: 'p',
        text: 'O administrador muda o tempo, ou desliga, em **Configurações → Pausas → Ausência automática**.',
      },
    ],
    audience: 'staff',
  },
  {
    id: 'supervisao-do-atendimento',
    date: '2026-11-04',
    title: 'Supervisão: a fila e os tempos das conversas',
    summary:
      'Uma tela para quem supervisiona as conversas: quem espera na fila, quem atende o quê, quem está sem resposta e os tempos de atendimento do dia e do período.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** os relatórios mediam os {tickets}, e quase nada das conversas.',
      },
      { kind: 'p', text: '**Agora**, no menu **Supervisão** (no Simplificado, uma aba de **Conversas**):' },
      {
        kind: 'list',
        items: [
          '**Agora:** a fila (a espera mais antiga primeiro), as conversas em atendimento (a sem resposta há mais tempo primeiro) e a equipe, com o status de cada um. A tela se atualiza sozinha a cada 15 segundos;',
          'a cor diz a espera: até 5 minutos é normal, de 5 a 15 é atenção, e acima de 15 é alerta;',
          '**Período:** conversas abertas e encerradas, as sem atendimento, a espera na fila, a 1ª resposta, o tempo de resposta e o de atendimento, por mesa e por atendente, com a lista das conversas e o CSV;',
          'o filtro de **Mesas** separa atendimento, suporte e financeiro. Cada número tem um (i) que diz como é contado.',
        ],
      },
      {
        kind: 'p',
        text: 'Quem vê: o administrador, e quem estiver num grupo de permissão com **Acompanhar o atendimento**, sempre nas mesas que a pessoa enxerga.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['attendance.monitor'] },
  },
  {
    id: 'grupo-de-permissao-obrigatorio',
    date: '2026-11-03',
    title: 'Cada atendente no seu grupo de permissão',
    summary:
      'A empresa pode exigir que todo atendente esteja num grupo de permissão. Quem estiver fora de todos vê um aviso para pedir o grupo ao administrador.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** o atendente fora de qualquer grupo de permissão via todas as mesas e quase todas as telas.',
      },
      {
        kind: 'list',
        items: [
          'com a exigência ligada, quem está fora de todos os grupos **não acessa nada** e vê o aviso "Você ainda não faz parte de um grupo de permissão";',
          'o administrador inclui a pessoa em **Configurações → Grupos de permissões**, ou no grupo de atendentes que o grupo de permissão usa;',
          'os administradores continuam com acesso a tudo.',
        ],
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['permission_groups.manage'] },
  },
  {
    id: 'conversas-por-mesa',
    date: '2026-11-03',
    title: 'Cada conversa com quem atende a mesa dela',
    summary:
      'A conversa ligada a um {ticket} fica com quem atende a mesa do {ticket}. O responsável sempre vê a dele.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** a conversa seguia só o departamento por onde entrou, e a conversa sem departamento aparecia para todos.',
      },
      { kind: 'p', text: '**Agora**, em **Chats**, no **Hoje** e nas conversas do {ticket}:' },
      {
        kind: 'list',
        items: [
          'a conversa ligada a um {ticket} é de quem atende a **mesa do {ticket}**, mesmo que tenha chegado por outro departamento;',
          'quem é o **responsável** pela conversa sempre a vê, em qualquer mesa;',
          'a conversa sem departamento e sem {ticket} fica com o responsável e com os administradores.',
        ],
      },
      {
        kind: 'p',
        text: 'Quem atende cada mesa se define nas configurações da mesa, em **Grupos de atendentes**. Mesa sem grupo continua visível a todos.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['chat.access'] },
  },
  {
    id: 'dia-na-conversa-e-print-na-comunicacao',
    date: '2026-11-03',
    title: 'O dia na conversa e o print colado na Comunicação',
    summary:
      'A conversa mostra de que dia é cada mensagem, e Ctrl+V no texto da Comunicação anexa o print, que pode sair sem texto.',
    body: [
      {
        kind: 'p',
        text: '**Na conversa**, uma linha com o dia (**Hoje**, **Ontem** ou a data) abre a conversa e aparece a cada troca de dia. Passe o mouse na hora de uma mensagem para ver a data e a hora completas.',
      },
      {
        kind: 'p',
        text: '**Na aba Comunicação do {ticket}**, tire o print e aperte **Ctrl+V** dentro do campo de texto: o print entra como anexo, sem salvar arquivo. Colar texto continua colando o texto.',
      },
      {
        kind: 'list',
        items: [
          'a comunicação pode sair **só com o anexo**, sem texto;',
          'para **Enviar por e-mail ao solicitante**, escreva o texto: o e-mail não leva o anexo.',
        ],
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['tickets.view_open', 'chat.access'] },
  },
  {
    id: 'meus-tickets-print-e-apontamento-aberto',
    date: '2026-10-27',
    title: 'Meus tickets, print colado e o apontamento aberto',
    summary:
      'Um botão mostra só os {tickets} em que você é o responsável, Ctrl+V anexa um print, e a descrição do apontamento aparece inteira.',
    body: [
      {
        kind: 'p',
        text: '**Na lista de {tickets}**, ao lado de **Todos abertos**, **Fechados** e **Cancelados**:',
      },
      {
        kind: 'list',
        items: [
          '**Meus tickets** mostra só os {tickets} em que você é o responsável, na mesa escolhida, na Lista e no Kanban;',
          'ele soma com a busca e com a situação, e um segundo clique desliga.',
        ],
      },
      {
        kind: 'p',
        text: '**Nos anexos do {ticket}**, tire o print e aperte **Ctrl+V** com a tela do {ticket} aberta: o print entra como anexo, sem salvar arquivo. Dentro de um campo de texto, o Ctrl+V continua sendo do campo. Nos {tickets} espelhados de outro sistema, o anexo continua sendo feito lá.',
      },
      {
        kind: 'p',
        text: '**Na aba Apontamentos**, a descrição aparece inteira logo abaixo de cada apontamento, sem abrir a edição.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['tickets.view_open'] },
  },
  {
    id: 'mencao-na-comunicacao-interna',
    date: '2026-10-06',
    title: 'Menção com @ na comunicação interna',
    summary:
      'Na comunicação interna, @ abre a lista da equipe. Quem você menciona recebe no sino e, se quiser, por e-mail.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** para chamar alguém num {ticket}, era preciso avisar por fora, e a nota interna não chegava a ninguém em especial.',
      },
      { kind: 'p', text: '**Agora**, com **Interno** ligado, na aba **Comunicação** do {ticket} ou na nota interna do atendimento:' },
      {
        kind: 'list',
        items: [
          'escreva **@** e o começo do nome: a lista traz só a equipe, e a setinha e o Enter escolhem;',
          'quem não abre este {ticket}, ou não vê a comunicação interna, aparece em cinza com o motivo e não pode ser escolhido;',
          'a pessoa mencionada recebe **Menção na comunicação interna** no sino e, se ligar em **Minha conta › Notificações**, por e-mail;',
          'no texto, o nome mencionado aparece destacado.',
        ],
      },
      {
        kind: 'p',
        text: 'A comunicação interna continua só da equipe: a menção nunca aparece no portal.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['internal_comm.view_add'] },
  },
  {
    id: 'emails-por-evento',
    date: '2026-10-06',
    title: 'E-mail por evento: escolha o que chega na sua caixa',
    summary:
      'Em Minha conta › Notificações, a coluna E-mails ao lado de Notificações escolhe evento por evento, em todo {ticket} que você vê ou só nos seus.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** um interruptor só mandava por e-mail os eventos dos {tickets} em que você era o responsável, e não havia como escolher quais.',
      },
      { kind: 'p', text: '**Agora**, em **Minha conta › Notificações**:' },
      {
        kind: 'list',
        items: [
          'cada evento tem duas linhas: **(geral)**, todo {ticket} que você enxerga, e **nos meus tickets**, os em que você é o responsável;',
          'a coluna **Notificações** vale para o sino, e a coluna **E-mails** para a sua caixa;',
          'a **comunicação interna** ganhou linha própria, separada da comunicação pública;',
          'o geral por e-mail começa desligado. Quem já recebia e-mail continua recebendo o mesmo.',
        ],
      },
      {
        kind: 'p',
        text: 'Uma notificação gera um e-mail só, mesmo marcada nas duas linhas, e nada chega do que você mesmo fez.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['tickets.view_open'] },
    action: { label: 'Ver novidade', to: '/account', search: { tab: 'notificacoes' }, startsTour: true },
    tour: {
      id: 'tour-emails-por-evento',
      steps: [
        {
          anchor: 'conta-notificacoes-tabela',
          icon: 'list',
          title: 'Geral e nos meus tickets',
          text: 'Cada evento tem duas linhas. Com o geral ligado, a linha dos seus {tickets} já vem incluída.',
        },
        {
          anchor: 'conta-notificacoes-emails',
          icon: 'bell',
          title: 'A coluna E-mails',
          text: 'Ligue só o que deve chegar à sua caixa. O geral por e-mail começa desligado.',
        },
      ],
    },
  },
  {
    id: 'notificacoes-no-servidor',
    date: '2026-10-06',
    title: 'Notificações: o lido segue você, e o sino ganhou Últimas respostas',
    summary:
      'O sino conta até 99+ e lembra o que você já leu em qualquer máquina. Clicar numa notificação marca só ela, e a página Ver notificações traz o histórico com filtros.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** o "já li" ficava no navegador, e o contador voltava cheio em outro computador. O sino mostrava as últimas 20 e parava em 9+.',
      },
      { kind: 'p', text: '**Agora**, no sino do topo:' },
      {
        kind: 'list',
        items: [
          'o contador vem do servidor, até **99+**, e não conta o que você mesmo fez;',
          'clicar numa notificação marca só ela como lida; **Marcar como lidas** lê todas as que estavam na tela;',
          'a aba **Últimas respostas** mostra as comunicações recentes nos {tickets} em que você é o responsável;',
          '**Ver notificações** abre o histórico, com filtro por tipo e por lidas e não lidas, e **Configurações** leva às suas preferências.',
        ],
      },
      {
        kind: 'p',
        text: 'O aviso do navegador passou a trazer o tipo, o número, o título, a {desk} e quem fez.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['tickets.view_open'] },
    action: { label: 'Ver notificações', to: '/notificacoes', startsTour: true },
    tour: {
      id: 'tour-notificacoes',
      steps: [
        {
          anchor: 'sino',
          icon: 'bell',
          title: 'O sino conta até 99+',
          text: 'O número é o das notificações que você ainda não leu, em qualquer máquina. Clicar numa delas marca só ela.',
        },
        {
          anchor: 'notificacoes-filtros',
          icon: 'filter',
          title: 'O histórico, com filtros',
          text: 'Escolha o tipo e se quer ver só as não lidas. Cada linha leva ao {ticket}.',
        },
        {
          anchor: 'notificacoes-marcar-lidas',
          icon: 'list',
          title: 'Marcar como lidas',
          text: 'Lê de uma vez tudo o que chegou até agora. As preferências de cada tipo ficam em Configurações.',
        },
      ],
    },
  },
  {
    id: 'rotina-diaria-do-recurso',
    date: '2026-10-06',
    title: 'O Recurso entra sozinho nos tickets que vieram do {source}',
    summary:
      'Toda noite, uma rotina liga o Recurso dos tickets abertos que trazem o código do {source} e preenche só o que está vazio na ficha. O que já estava preenchido não muda.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** os tickets importados com o código do {source} na descrição ficavam sem Recurso, e a ficha dependia de alguém escolher o {resource} à mão.',
      },
      { kind: 'p', text: '**Agora**, das 3h às 6h, a rotina lê o {resource} e o {contact} no {source} e:' },
      {
        kind: 'list',
        items: [
          'liga o Recurso do ticket, quando ele ainda está vazio;',
          'preenche os campos vazios da ficha, uma vez só;',
          'nunca troca um valor que a descrição, o título ou uma pessoa já trouxeram.',
        ],
      },
      {
        kind: 'p',
        text: 'Na ficha, o que veio da rotina aparece como **do {source}**, com a data e "rotina diária". No Histórico, o autor é **Rotina diária**. Se o cadastro mudar depois no {source}, o cartão da ficha avisa quantos campos mudaram, e o **Atualizar do {source}** traz a versão nova.',
      },
      {
        kind: 'p',
        text: 'Quem administra acompanha a última rodada em **Configurações › Técnicos**, no bloco **Rotina diária do Recurso**.',
      },
    ],
    audience: 'staff',
    requires: { capabilities: ['sourceIntegration'], resourceSources: ['ticket_identifier'] },
  },
  {
    id: 'usuarios-equipe-e-portal',
    date: '2026-10-06',
    title: 'Usuários: a equipe separada dos clientes do portal',
    summary:
      'A tela de Usuários ganhou as abas Equipe, Clientes do portal e Todos. A chave na linha redefine a senha, e cada login de portal mostra de qual {client} ele é.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** a equipe e os logins de portal dividiam uma lista só, e a nova senha digitada no cadastro não chegava ao login da pessoa.',
      },
      { kind: 'p', text: '**Agora**, em **Configurações › Usuários**:' },
      {
        kind: 'list',
        items: [
          'as abas **Equipe**, **Clientes do portal** e **Todos** separam quem atende de quem abre chamado pelo portal;',
          'a chave na linha (**Redefinir senha**) gera uma senha temporária, mostrada uma vez, com **Copiar** e **Copiar mensagem**. A senha antiga para de valer na hora, e a pessoa cria a dela no primeiro acesso;',
          'a coluna **{Client}** mostra de qual {client} é cada login de portal, com **+N** quando há mais de um vínculo. No cadastro do login, a seção **{Clients} deste login** liga e desliga os vínculos.',
        ],
      },
    ],
    audience: 'staff',
    requires: { roles: ['admin'], anyPermission: ['users.manage'] },
    action: { label: 'Ver novidade', to: '/settings/users', startsTour: true },
    tour: {
      id: 'tour-usuarios',
      steps: [
        {
          anchor: 'usuarios-filtro',
          icon: 'filter',
          title: 'Equipe e clientes do portal, separados',
          text: 'Escolha a aba para ver só a equipe, só os logins de portal ou todo mundo.',
        },
        {
          anchor: 'usuarios-coluna-cliente',
          icon: 'users',
          title: 'De qual {client} é cada login',
          text: 'Cada login de portal mostra aqui de qual {client} ele é, com +N quando há mais de um vínculo. No cadastro dele, você liga e desliga os vínculos.',
        },
        {
          anchor: 'usuarios-redefinir-senha',
          icon: 'key',
          title: 'Redefinir senha',
          text: 'A chave gera uma senha temporária. Ela aparece uma vez só: copie a mensagem pronta e mande para a pessoa.',
        },
      ],
    },
  },
  {
    id: 'busca-no-sistema-de-origem',
    date: '2026-10-06',
    title: 'Busca por {resourceId} no {source} ao abrir um ticket',
    summary:
      'No Novo ticket e no campo Recurso, você busca por {resourceId}, nome ou CPF direto no {source}. Escolher o {resource} preenche a ficha e liga o Recurso.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** o ticket era aberto digitando {resourceId} e preenchendo a ficha à mão, copiando do {source}.',
      },
      { kind: 'p', text: '**Agora:**' },
      {
        kind: 'list',
        items: [
          'no **Novo ticket**, o campo **{ResourceId} (busca no {source})** procura por {resourceId}, nome ou CPF do {contact}. Escolher um resultado preenche a ficha e liga o Recurso, e o campo **{Client}** vem sugerido pelo ponto de venda;',
          'no ticket, o campo **Recurso** busca do mesmo jeito;',
          'o {resource} e o {contact} ganharam página própria, com os tickets de cada um: os links **Ver {resource}** e **Ver {contact}** ficam no cartão da ficha;',
          'o que veio do {source} aparece marcado **do {source}**, e o **Atualizar do {source}** relê o cadastro quando ele muda lá.',
        ],
      },
    ],
    // demonstração local, com o SGR falso do repositório (veículos e pessoas inventados)
    images: [
      {
        src: '/novidades/busca-resultados.png',
        alt: 'A busca no {source} no Novo ticket: os resultados com {resourceId}, modelo e nome, e a situação de cada um',
      },
      {
        src: '/novidades/busca-no-novo-ticket.png',
        alt: 'A ficha no Novo ticket, com o campo de busca no {source} e o link Mais dados da ficha',
      },
    ],
    audience: 'staff',
    requires: { capabilities: ['sourceIntegration'], anyPermission: ['tickets.view_open'] },
    action: { label: 'Ver novidade', to: '/tickets', search: { new: 1 }, startsTour: true },
    tour: {
      id: 'tour-novo-ticket-busca',
      steps: [
        {
          anchor: 'novo-ticket-busca-origem',
          icon: 'search',
          title: 'Busca no {source}',
          text: 'Digite {resourceId}, nome ou CPF do {contact}. Os resultados chegam do {source} em poucos segundos.',
        },
        {
          anchor: 'novo-ticket-ficha',
          icon: 'list',
          title: 'A ficha se preenche sozinha',
          text: 'Ao escolher um resultado, a ficha recebe os dados do {source} e o Recurso fica ligado. Os campos continuam editáveis.',
        },
        {
          anchor: 'novo-ticket-mais-dados',
          icon: 'sparkles',
          title: 'Mais dados da ficha',
          text: 'Endereço, cidade e os outros campos ficam aqui. O que veio do {source} já chega preenchido.',
        },
      ],
    },
  },
  {
    id: 'ficha-em-campos',
    date: '2026-10-05',
    title: 'A ficha do {contact} virou campos de verdade',
    summary:
      'Telefone, nome, {resourceId} e endereço agora são campos do ticket, e cada valor diz de onde veio. O lápis corrige sem mexer na descrição.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** a ficha era só texto na descrição. O ticket aberto à mão não tinha ficha, e um telefone esquecido fazia o WhatsApp da mesa não sair.',
      },
      { kind: 'p', text: '**Agora:**' },
      {
        kind: 'list',
        items: [
          'em **Informações gerais**, o cartão **Ficha do {contact}** mostra cada campo e a origem dele: a descrição, o título ou quem editou;',
          'o lápis corrige um campo, e **voltar ao da descrição** desfaz a correção;',
          'o **Novo ticket** traz os campos da ficha, e o resto fica em **Mais dados da ficha**;',
          'na mesa que manda WhatsApp ao abrir, o ticket aberto à mão pede o telefone com DDD e o que mais o modelo usar. Quem configura a mesa liga e desliga isso em **Exigir a ficha ao abrir ticket à mão**.',
        ],
      },
      { kind: 'p', text: 'A correção aparece no Histórico do ticket e também no portal.' },
    ],
    audience: 'staff',
    requires: { resourceSources: ['ticket_identifier'], anyPermission: ['tickets.view_open'] },
  },
  {
    id: 'cotacao-de-deslocamento',
    date: '2026-10-05',
    title: 'Cotação de deslocamento: o km pelo mapa do Google',
    summary:
      'Na aba Valores, a Nova cotação calcula o km de ida e de volta no mapa do Google, sugere os endereços enquanto você digita e lança tudo de uma vez.',
    body: [
      { kind: 'p', text: '**Antes:** o deslocamento era calculado fora do ERP e lançado à mão em **+ Lançamento**.' },
      { kind: 'p', text: '**Agora**, no ticket, em **Valores › Nova cotação**:' },
      {
        kind: 'list',
        items: [
          'origem, destino e retorno ganham sugestões de endereço enquanto você digita, e o destino já vem com o endereço da ficha;',
          '**Calcular** mede o km de cada perna e desenha a rota no mapa. Os marcadores se arrastam para acertar o ponto;',
          'o total cobra o km de cada perna, ou o **valor cheio** que você digitar na linha do total;',
          '**Lançar** cria os lançamentos na aba e guarda a cotação.',
        ],
      },
      {
        kind: 'p',
        text: 'Endereço fora do Brasil é recusado, e perna com mais de 500 km pede conferência. Depois de 29 dias, a cotação guarda os valores, mas deixa de mostrar o mapa.',
      },
    ],
    audience: 'staff',
    requires: { capabilities: ['maps'], anyPermission: ['tickets.edit'] },
  },
  {
    id: 'responsavel-so-da-equipe',
    date: '2026-10-04',
    title: 'Responsável: a busca mostra só a equipe',
    summary:
      'O campo Responsável e os filtros de pessoa listam só quem é da equipe, com busca por nome ou e-mail. Os logins de portal saíram da lista.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** o **Responsável** listava também os logins de portal, e a lista parava em 200 nomes.',
      },
      { kind: 'p', text: '**Agora** o seletor busca só na equipe, como o do técnico responsável:' },
      {
        kind: 'list',
        items: [
          'digite o nome ou o e-mail para filtrar;',
          '**Mostrar inativos** traz quem saiu da equipe, para os tickets antigos;',
          '**Sem responsável** deixa o ticket sem dono.',
        ],
      },
      {
        kind: 'p',
        text: 'Vale no Novo ticket, em Informações gerais e nos filtros de pessoa de Apontamentos, Atendimentos, Agenda e da transferência de conversa.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['tickets.view_open'] },
  },
  {
    id: 'historico-detalhado',
    date: '2026-10-03',
    title: 'Histórico do ticket: o que mudou, campo a campo',
    summary:
      'Cada edição no ticket aparece no Histórico com o campo, o valor de antes e o de depois, e não mais só "Ticket atualizado".',
    body: [
      { kind: 'p', text: '**Antes:** o Histórico dizia só **Ticket atualizado**, sem contar o quê.' },
      {
        kind: 'p',
        text: '**Agora**, cada edição traz uma linha por campo, no formato **Campo: antes → depois**, com quem fez e quando:',
      },
      {
        kind: 'list',
        items: [
          'texto longo, como a descrição, abre em **Ver o antes e depois**;',
          'a ficha, os apontamentos e o técnico também entram;',
          'as edições de antes da mudança mostram **Alterou:** e o nome do campo.',
        ],
      },
      {
        kind: 'p',
        text: 'Fica na aba **Histórico** do ticket. Na visão Simplificada, as edições aparecem agrupadas na linha do tempo do atendimento.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['tickets.view_open'] },
  },
  {
    id: 'pdf-por-atendente',
    date: '2026-10-03',
    title: 'Extrato de apontamentos em PDF, por atendente',
    summary:
      'O extrato de apontamentos sai em PDF: o período inteiro, com cada atendente numa página nova, ou só uma pessoa pelo botão PDF na linha de total dela.',
    body: [
      {
        kind: 'p',
        text: '**Antes:** o extrato só saía em CSV, e o relatório de cada atendente era montado na planilha.',
      },
      { kind: 'p', text: '**Agora**, em **Relatórios › Extrato de apontamentos**, depois de **Exibir**:' },
      {
        kind: 'list',
        items: [
          '**Baixar PDF** gera o período inteiro: um resumo e cada atendente a partir de uma página nova;',
          'o botão **PDF** na linha **Total** de cada atendente gera o extrato só daquela pessoa.',
        ],
      },
      {
        kind: 'p',
        text: 'O PDF vai até 2.000 linhas. Quando o período do time inteiro passa disso, escolha os atendentes ou encurte o período.',
      },
    ],
    audience: 'staff',
    requires: { anyPermission: ['reports.attendants'] },
    action: { label: 'Ver novidade', to: '/reports', search: { report: 'extract' }, startsTour: true },
    tour: {
      id: 'tour-pdf-por-atendente',
      steps: [
        {
          anchor: 'relatorios-atendentes',
          icon: 'users',
          title: 'Escolha os atendentes',
          text: 'Deixe todos para o extrato do time, ou marque só quem deve entrar no PDF.',
        },
        {
          anchor: 'relatorios-exibir',
          icon: 'list',
          title: 'Exiba o período',
          text: 'O PDF sai do que está na tela: escolha o período e clique em Exibir.',
        },
        {
          anchor: 'relatorios-baixar-pdf',
          icon: 'file',
          title: 'Baixar PDF',
          text: 'O período inteiro, com um resumo e cada atendente a partir de uma página nova.',
        },
        {
          anchor: 'relatorios-pdf-atendente',
          icon: 'file',
          title: 'O PDF de uma pessoa só',
          text: 'Na linha Total de cada atendente, o botão PDF gera o extrato só dela.',
        },
      ],
    },
  },
];
